<template>
  <div class="page-info">
    <targetComponent :breadcrumb-segments="getInfoPageSegments()" :article-analytics-data="articleAnalyticsData"></targetComponent>
  </div>
</template>

<script setup>

const appConfig = useAppConfig();
const page = usePage().value.page;
const entity = useEntity();
const i18n = useI18n();
const locale = useLocale().getLocale();

let moduleName;

switch (entity.type) {
  case appConfig.infoPageTypes.magazine:
    moduleName = 'magazine';
    break;
  default:
    moduleName = 'generic';
    break;
}

// custom components overrides for specific pages

switch (page.id) {
  case 3453:
    moduleName = 'contact';
    break;
  case 3449:
    moduleName = 'loyaltyProgram';
    break;
}

//#region structured data

const customStructuredData = [
  {
    key: 'Article/headline',
    value: entity.page.header
  },
  {
    key: 'Article/description',
    value: entity.annotation
  }
];

if (moduleName === 'magazine') {
  customStructuredData.push({
    key: 'Article/image',
    value: `${useShopZone().value.staticResourcesRoot}/img/magazine/${entity.id}.jpg`
  });
}

if (entity.created) {
  customStructuredData.push({
    key: 'Article/datePublished',
    value: entity.created
  });
}

if (entity.page.publishedAt) {
  customStructuredData.push({
    key: 'Article/dateModified',
    value: entity.page.publishedAt
  });
}

const structuredDataObjString = useStructuredData().getStructuredDataForPage(page.structuredData, customStructuredData);

useHead({
  script: [{
    type: 'application/ld+json',
    innerHTML: structuredDataObjString
  }],
  __dangerouslyDisableSanitizers: ['script'],
});

//#endregion

// try to fetch custom content

if (appConfig.infoPageOverridenIds.includes(page.id)) {

  let infoPageUrl = `https://${useShopRouting().getShopZone(useRequestURL().host).domain}/infoPages/${page.id}-${locale.languageId}.html?v=001`;

  if (process.env.NODE_ENV === 'development') {
    infoPageUrl = `http://localhost:3000/infoPages/${page.id}-${locale.languageId}.html`;
  }

  const response = await fetch(infoPageUrl);

  if (response.ok) {
    const customContent = await response.text();
    if (customContent) {
      entity.content = customContent;
    }
  }
}

const articleAnalyticsData = {
  article_id: entity.id,
  article_type: entity.type === appConfig.infoPageTypes.magazine ? 'Magazín' : (entity.type === appConfig.infoPageTypes.lexicon ? 'Slovník pojmů' : 'Informační stránka'),
  article_title: entity.page.header,
  article_author: entity.author,
  article_category: entity.magazineCategory?.name,
  article_reading_time: entity.timeToRead?.toString(),
  article_published: entity.page.publishedAt,
  article_tags: entity.filterProperties.map(property => property.value).join(',')
};

let targetComponent = defineAsyncComponent(
  () => import(`../templates/infoPage/${moduleName}.vue`)
);

const getInfoPageSegments = () => {

  const result = entity.parents.map(parent => {
    return {
      level: parent.level,
      name: parent.page.id === 18438 ? i18n.t('Magazín') : (parent.page.id === 18437 ? i18n.t('Slovník pojmů') : parent.page.title),
      url: `${parent.page.url}`
    }
  });

  result.push({
    level: 0,
    name: entity.page.header
  });

  return result;
}


</script>

<style lang="postcss">
.page-info {
  &__heading {
    @apply my-8 py-8 bg-neutral-100;

    h1 {
      @apply text-4xl font-bold tracking-normal text-center;
    }
  }
}

.page-info {

  &__content,
  &--loyalty-program {

    @apply pb-8;

    .mag {
      &-perex {
        @apply my-4 text-[1.05rem] leading-6 text-justify font-bold;
      }
    }

    p {
      @apply my-4 leading-7 text-lg text-justify indent-6;

      &.no-indent {
        @apply indent-0;
      }
    }

    h2 {
      @apply my-4 font-bold tracking-normal text-2xl;
    }

    h3 {
      @apply my-4 font-bold tracking-normal text-lg;
    }

    ul,
    ol {
      @apply my-4 leading-8 list-disc list-inside tablet:pl-10;
    }

    iframe,
    img {
      @apply inline-block;
    }

    img {
      @apply my-4;
    }

    a {
      @apply text-secondary-500 hover:no-underline;

      &[href] {
        @apply underline hover:underline;
      }
    }
  }

  &__wrapper {
    @apply max-w-[740px] mx-auto px-4;
  }

  &__lexicon-navigation {
    @apply flex flex-wrap justify-between gap-x-8 gap-y-6 mb-10 px-4 py-6 tracking-normal bg-neutral-100 tablet:p-6 desktop:p-8;

    &__link {
      @apply w-full flex gap-4 tablet:flex-1;

      &:last-of-type {
        @apply justify-end text-right;
      }

      &:hover {
        @apply no-underline;
      }
    }

    &__label {
      @apply text-lg font-bold underline;
    }

    &__separator {
      @apply w-full border-t border-neutral-300 tablet:w-0 tablet:border-t-0 tablet:border-l;
    }
  }

  &--vendors {

    .page-info__wrapper {
      @apply max-w-[1000px] text-center;

      p:first-of-type {
        @apply max-w-[660px] mx-auto text-justify;
      }

      h2 {
        @apply mt-16 mb-8 font-bold tracking-normal text-2xl text-primary-500;

        a {
          @apply hover:no-underline;
        }
      }
    }
  }

  &--information-center {
    .page-info__wrapper {
      @apply max-w-[1700px] text-center;
    }

    .page-info__heading {
      @apply hidden;
    }

    .information-center {
      @apply gap-6 desktop:flex desktop:text-left;

      &>.sections {
        @apply w-full self-start mb-6 border-t-8 border-primary-500 bg-neutral-100 p-4 pb-2 tablet:px-8 desktop:sticky desktop:top-[100px] desktop:mb-0 desktop:p-10 desktop:pb-6 desktop:w-[330px];

        &>.h {
          @apply font-bold tracking-normal text-2xl desktop:text-xl;
        }

        &>ul {
          @apply flex flex-wrap p-4 pb-2 justify-center desktop:block;
        }

        a {
          @apply block my-1 text-lg font-bold tracking-normal text-primary-500 underline desktop:my-2;
        }

        &>ul>li {
          @apply relative mx-4 desktop:mx-0;

          &:after {
            @apply absolute -right-4 top-1 bottom-0.5 content-[''] border-r border-neutral-300 desktop:hidden;
          }
        }

        &>ul>li:last-child:after {
          @apply hidden;
        }

        .end-of-line:after {
          @apply hidden;
        }
      }

      &>.content {
        @apply flex-1;

        h2 {
          @apply font-bold tracking-normal text-2xl text-primary-500;
        }

        p {
          @apply mt-6 mb-8;
        }

        ul {
          @apply flex flex-wrap justify-center gap-4 mb-16 desktop:justify-start;

          li {
            @apply flex flex-col w-full border border-neutral-300 tablet:w-[calc(33.3%-0.655rem)] desktop:w-[calc(25%-0.75rem)];

            &:hover {
              a:first-of-type {
                @apply text-neutral-400;
              }

              a:last-of-type {
                @apply bg-neutral-400;
              }
            }

            hr {
              @apply hidden;
            }

            a:hover {
              @apply no-underline;
            }

            a:first-of-type {
              @apply p-6 text-[7rem] text-center transition-colors;
            }

            a:last-of-type {
              @apply flex-1 flex items-center justify-center p-6 text-lg font-bold text-center text-neutral-50 bg-neutral-500 transition-colors desktop:py-8;
            }
          }
        }

        .icon {
          @apply max-w-[100px];
        }

      }

      .cleaner {
        @apply hidden;
      }
    }
  }

  &--branch {
    .page-info__wrapper {
      @apply max-w-[910px];
    }

    p,
    h2 {
      @apply mb-6;
    }

    h2 {
      @apply font-bold tracking-normal text-2xl text-primary-500;
    }

    p {
      @apply text-justify;
    }

    a {
      @apply underline text-primary-500;

      &.secondary {
        @apply text-secondary-500;
      }
    }

    &__box {
      @apply my-4 py-4 border-t border-b border-neutral-300 desktop:py-6 desktop:my-6;
    }
  }

  &--loyalty-program {
    .page-info__heading {
      .page-info__wrapper {
        @apply max-w-[840px];
      }
    }

    h2 {
      @apply flex items-center my-6 text-3xl text-primary-500;

      &:after {
        @apply block w-20 h-1 bg-primary-500 content-[''];
      }
    }

    h3 {
      @apply my-6 text-xl;
    }
  }

  &--services {
    .page-info__wrapper {
      @apply max-w-[1400px];
    }

    a {
      @apply !text-neutral-500 !no-underline;
    }
  }
}
</style>
